import {
  splashBackground01,
  splashBackground02,
  splashBackground03,
  splashBackground04,
  splashBackgroundTablet01,
  splashBackgroundTablet02,
  splashBackgroundTablet03,
  splashBackgroundTablet04,
} from '@/assets/images';
import { useScaling } from '@/hooks/use-scaling';

import { useEffect, useState } from 'react';
import { AppState, ImageSourcePropType } from 'react-native';

const phoneBackgrounds: ImageSourcePropType[] = [
  splashBackground01,
  splashBackground02,
  splashBackground03,
  splashBackground04,
];

const tabletBackgrounds: ImageSourcePropType[] = [
  splashBackgroundTablet01,
  splashBackgroundTablet02,
  splashBackgroundTablet03,
  splashBackgroundTablet04,
];

function getTodayKey(): string {
  const now = new Date();
  return `${now.getFullYear()}-${now.getMonth() + 1}-${now.getDate()}`;
}

// Deterministic index from the local date so every screen renders the same
// background within the same day, and the pick rotates at local midnight.
function pickIndex(dateKey: string, modulo: number): number {
  let hash = 0;
  for (let i = 0; i < dateKey.length; i++) {
    hash = (hash * 31 + dateKey.charCodeAt(i)) % 2147483647;
  }
  return hash % modulo;
}

function msUntilNextMidnight(): number {
  const now = new Date();
  const next = new Date(now);
  next.setHours(24, 0, 0, 0);
  return next.getTime() - now.getTime();
}

export function useDailyBackground(): ImageSourcePropType {
  const { isTablet } = useScaling();
  const [dateKey, setDateKey] = useState(getTodayKey);

  useEffect(() => {
    const refresh = () => {
      const today = getTodayKey();
      setDateKey(prev => (prev === today ? prev : today));
    };

    // Schedule a refresh right after local midnight. A small buffer avoids
    // firing one tick before the date actually rolls over.
    const timer = setTimeout(refresh, msUntilNextMidnight() + 1000);

    // Backgrounded JS timers can be suspended (esp. on iOS), so re-check whenever
    // the app comes back to the foreground.
    const sub = AppState.addEventListener('change', state => {
      if (state === 'active') refresh();
    });

    return () => {
      clearTimeout(timer);
      sub.remove();
    };
  }, [dateKey]);

  const pool = isTablet ? tabletBackgrounds : phoneBackgrounds;
  return pool[pickIndex(dateKey, pool.length)];
}
